/**
 * Оценка надёжности пароля на стороне браузера.
 *
 * Тот же алгоритм zxcvbn, что и на сервере: если бы фронтенд считал по своим
 * правилам, форма показывала бы «хороший пароль», а регистрация отклонялась.
 * Сервер остаётся источником правды, здесь только подсказка в реальном времени.
 *
 * Словари весят заметно, поэтому загружаются лениво: страницы без формы
 * регистрации их не тянут.
 */

import i18next from "i18next";

export const MAX_SCORE = 4;

export interface Strength {
  score: number;
  label: string;
  /** Проходит ли пароль минимальный порог сервера. */
  acceptable: boolean;
  hint: string | null;
}

// Ярлыки живут в словаре: подпись под полем видит пользователь.
const LABEL_KEYS = [
  "password.veryWeak",
  "password.weak",
  "password.fair",
  "password.good",
  "password.strong",
];

function label(score: number): string {
  return i18next.t(LABEL_KEYS[score] ?? LABEL_KEYS[0]);
}

type Zxcvbn = (password: string, userInputs?: string[]) => { score: number };

let loading: Promise<Zxcvbn> | null = null;

/** Загружает движок оценки один раз на всё приложение. */
async function loadZxcvbn(): Promise<Zxcvbn> {
  loading ??= (async () => {
    const [core, common] = await Promise.all([
      import("@zxcvbn-ts/core"),
      import("@zxcvbn-ts/language-common"),
    ]);

    core.zxcvbnOptions.setOptions({
      dictionary: { ...common.dictionary },
      graphs: common.adjacencyGraphs,
    });

    return (password: string, userInputs: string[] = []) =>
      core.zxcvbn(password, userInputs);
  })();

  return loading;
}

/**
 * Повторяет правило сервера: пароль не должен содержать имя пользователя
 * или части адреса почты — такие подбирают в первую очередь.
 */
function containsUserData(password: string, userInputs: string[]): boolean {
  const lowered = password.toLowerCase();

  return userInputs
    .flatMap((input) => [input, ...input.split(/[^\p{L}\p{N}]+/u)])
    .map((token) => token.trim().toLowerCase())
    .filter((token) => token.length >= 4)
    .some((token) => lowered.includes(token));
}

/** Оценивает пароль. minScore приходит с сервера, чтобы пороги совпадали. */
export async function evaluate(
  password: string,
  userInputs: string[],
  minScore: number,
): Promise<Strength> {
  if (password.length === 0) {
    return { score: 0, label: "", acceptable: false, hint: null };
  }

  // Короткий пароль отклоняем сразу, не дожидаясь загрузки словарей.
  if (password.length < 8) {
    return {
      score: 0,
      label: label(0),
      acceptable: false,
      hint: i18next.t("password.tooShort"),
    };
  }

  const zxcvbn = await loadZxcvbn();
  const inputs = userInputs.filter(Boolean);
  let { score } = zxcvbn(password, inputs);

  if (containsUserData(password, inputs)) {
    score = Math.min(score, 1);
  }

  const acceptable = score >= minScore;

  return {
    score,
    label: label(score),
    acceptable,
    hint: acceptable ? null : hintFor(password, inputs),
  };
}

function hintFor(password: string, userInputs: string[]): string {
  if (containsUserData(password, userInputs)) {
    return i18next.t("password.hintPersonal");
  }
  if (password.length < 12) {
    return i18next.t("password.hintLonger");
  }
  return i18next.t("password.hintDictionary");
}
